/**
 * The game websocket: authenticates with a ticket, carries lobby JSON both ways, intents
 * up, acks / pongs / binary snapshots down. An unexpected drop is retried with backoff for
 * the server's seat hold (§3: 120 s), resuming the match seat with the resume token; the
 * state (`open` / `reconnecting` / `closed`) goes to every `onState` listener so the screens
 * can say so. `retry()` starts a fresh window (the RETRY button).
 */
import { encodeMessage, intentMessage, parseServerMessage, type ClientMessage, type ServerMessage } from '@sa/protocol';
import type { Intent } from '@sa/sim';
import type { MatchEvents, MatchLink } from '../game/link';
import { api, ApiError, wsUrl } from './api';

const RESUME_KEY = 'sa.resume';
const RECONNECT_MS = [500, 1000, 2000, 4000, 8000];
/** keep retrying this long after a drop (the server holds a match seat for 120 s) */
export const RESUME_WINDOW_MS = 120_000;

export type SocketState = 'connecting' | 'open' | 'reconnecting' | 'closed';

/**
 * Why the socket is closed: `lost` (the server stopped answering), `unauthorized` (the
 * session is gone), `replaced` (this account connected from another tab), `refused`
 * (the server closed us for misbehaving), `byUs` (sign-out).
 */
export type CloseReason = 'lost' | 'unauthorized' | 'replaced' | 'refused' | 'byUs';

export class GameSocket {
  private ws: WebSocket | null = null;
  private listeners = new Set<(m: ServerMessage) => void>();
  private stateListeners = new Set<(s: SocketState) => void>();
  private match: MatchEvents | null = null;
  /** match traffic that arrived before a game view bound itself (e.g. resume after reload) */
  private early: ((h: MatchEvents) => void)[] = [];
  private seq = 1;
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  state: SocketState = 'connecting';
  /** performance.now() when the link dropped (0 while open) */
  lostAt = 0;
  closeReason: CloseReason | null = null;

  constructor(private readonly windowMs = RESUME_WINDOW_MS) {}

  /** First connection: resolves once open; on failure the socket is `closed` (no retries) and this rejects. */
  async connect(): Promise<void> {
    try {
      await this.attempt();
    } catch (e) {
      this.close(reasonOf(e));
      throw e;
    }
  }

  /** One attempt (ticket, then socket): resolves when open, rejects on any failure. */
  private async attempt(): Promise<void> {
    this.setState(this.lostAt ? 'reconnecting' : 'connecting');
    const { ticket } = await api.ticket();
    await new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(wsUrl());
      ws.binaryType = 'arraybuffer';
      let opened = false;
      ws.onopen = () => {
        opened = true;
        this.ws = ws;
        const resumeToken = sessionStorage.getItem(RESUME_KEY) ?? undefined;
        ws.send(encodeMessage({ type: 'auth', ticket, ...(resumeToken ? { resumeToken } : {}) }));
        this.attempts = 0;
        this.lostAt = 0;
        this.closeReason = null;
        this.setState('open');
        resolve();
      };
      ws.onmessage = (e) => {
        if (this.ws === ws) this.onMessage(e.data);
      };
      ws.onclose = (e) => {
        if (!opened) reject(new Error('Connection failed'));
        else if (this.ws === ws) this.onClose(e.code);
      };
    });
  }

  private setState(s: SocketState): void {
    if (s === this.state) return;
    this.state = s;
    for (const f of this.stateListeners) f(s);
  }

  /** Listen to state changes; returns the unsubscribe function. */
  onState(fn: (s: SocketState) => void): () => void {
    this.stateListeners.add(fn);
    return () => this.stateListeners.delete(fn);
  }

  private toMatch(f: (h: MatchEvents) => void): void {
    if (this.match) f(this.match);
    else if (this.early.length < 1200) this.early.push(f);
  }

  private onMessage(data: ArrayBuffer | string): void {
    if (typeof data !== 'string') {
      this.toMatch((h) => h.snapshot(data));
      return;
    }
    const m = parseServerMessage(data);
    if (!m) return;
    if (m.type === 'welcome') {
      if (m.resumeToken) sessionStorage.setItem(RESUME_KEY, m.resumeToken);
      this.early = [];
      this.toMatch((h) => h.welcome(m));
    } else if (m.type === 'ack') this.toMatch((h) => h.ack(m.seq, m.ok, m.err));
    else if (m.type === 'pong') this.match?.pong(m.t, m.tick);
    else if (m.type === 'end') {
      sessionStorage.removeItem(RESUME_KEY);
      this.toMatch((h) => h.end(m));
    } else if (m.type === 'room.error' && m.code === 'resumeExpired') sessionStorage.removeItem(RESUME_KEY);
    for (const l of this.listeners) l(m);
  }

  private onClose(code: number): void {
    this.ws = null;
    if (code === 4401) return this.close('unauthorized');
    if (code === 4000) return this.close('replaced');
    if (code === 4008) return this.close('refused');
    // unexpected drop: retry while a resumable seat may still be held
    this.lostAt = performance.now();
    this.attempts = 0;
    this.reconnectSoon();
  }

  private reconnectSoon(): void {
    if (this.retryTimer) return;
    if (performance.now() - this.lostAt > this.windowMs) return this.close('lost');
    this.setState('reconnecting');
    const delay = RECONNECT_MS[Math.min(this.attempts++, RECONNECT_MS.length - 1)];
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.attempt().catch((e) => {
        const why = reasonOf(e);
        if (why === 'unauthorized') this.close(why);
        else this.reconnectSoon();
      });
    }, delay);
  }

  /** Try again now, with a fresh reconnect window (RETRY; also the menu's slow retry). */
  retry(): void {
    if (this.state === 'open' || this.state === 'connecting' || this.closeReason === 'byUs') return;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.lostAt = performance.now();
    this.attempts = 0;
    this.reconnectSoon();
  }

  /** Seconds since the link dropped (0 while open). */
  downSeconds(now = performance.now()): number {
    return this.lostAt ? (now - this.lostAt) / 1000 : 0;
  }

  on(fn: (m: ServerMessage) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  send(m: ClientMessage): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN) return false;
    this.ws.send(encodeMessage(m));
    return true;
  }

  /** The match side of this socket as a MatchLink (one per match). */
  matchLink(): MatchLink {
    const sock = this;
    return {
      local: false,
      tickMs: 100,
      bind(h) {
        sock.match = h;
        const q = sock.early;
        sock.early = [];
        q.forEach((f) => f(h));
      },
      send(intent: Intent) {
        const seq = sock.seq++;
        sock.send(intentMessage(intent, seq));
        return seq;
      },
      ping: (t) => sock.send({ type: 'ping', t }),
      close: () => (sock.match = null),
    };
  }

  /** Stop for good (`byUs`: sign-out) or until `retry()`. */
  close(reason: CloseReason = 'byUs'): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    this.closeReason = reason;
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000);
    this.setState('closed');
  }
}

function reasonOf(e: unknown): CloseReason {
  return e instanceof ApiError && e.status === 401 ? 'unauthorized' : 'lost';
}
